import * as d3 from "d3";
import * as shape from "d3-shape";

import { DEFAULT_MULTIPLIER, MIN_CONTRIBUTION_AGE } from "@constants";
import {
  BuildBarChartParams,
  BuildLinearChartParams,
  CalculateAmountPerYearParams,
  DataPoint,
  InflateChartDataParams,
  InvestedProgressionParams,
  LinearChartNode,
  LinearChartProps,
  PortfolioPerformanceGraphNode,
  ProjectedProgressionParams,
  SipFrequency,
  TrimChartDataParams,
} from "@ctypes";
import { constants } from "@layout";
import { scaleLinear } from "d3";

export const calculateAmountPerYear = ({
  amount,
  recurringDepositType,
}: CalculateAmountPerYearParams) => {
  if (recurringDepositType === SipFrequency.Annually) {
    return amount;
  }
  if (recurringDepositType === SipFrequency.Monthly) {
    return amount * 12;
  }
  if (recurringDepositType === SipFrequency.Daily) {
    return amount * 365;
  }

  return amount * 52;
};

export const calculateInvestedProgression = ({
  amount,
  recurringDepositType,
  currentAge,
  projectedAge,
  contributeAfter18,
  initialBalance = 0,
}: InvestedProgressionParams) => {
  const data = [];
  let balance = initialBalance;

  const amountPerYear = calculateAmountPerYear({
    amount,
    recurringDepositType,
  });

  for (let i = currentAge; i <= projectedAge; i++) {
    if (i <= MIN_CONTRIBUTION_AGE || (i > MIN_CONTRIBUTION_AGE && contributeAfter18)) {
      balance += amountPerYear;
    }

    data.push({ age: i, amount: balance });
  }

  return data;
};

export const calculateProjectedProgression = ({
  amount,
  recurringDepositType,
  currentAge,
  projectedAge,
  multiplier = DEFAULT_MULTIPLIER,
  contributeAfter18 = true,
  initialBalance = 0,
}: ProjectedProgressionParams) => {
  const data = [];
  let balance = initialBalance;

  const amountPerYear = calculateAmountPerYear({
    amount,
    recurringDepositType,
  });

  for (let i = currentAge; i <= projectedAge; i++) {
    balance *= multiplier;

    if (i <= MIN_CONTRIBUTION_AGE || (i > MIN_CONTRIBUTION_AGE && contributeAfter18)) {
      balance += amountPerYear;
    }

    data.push({ age: i, amount: balance });
  }

  return data;
};

export const buildBarChart = ({
  invested,
  projected,
  width = constants.chart.projection.width,
}: BuildBarChartParams) => {
  const xDomain = invested.map((data) => data.age.toString());
  const xRange = [0, width];
  const x = d3.scalePoint().domain(xDomain).range(xRange).padding(1);

  const yDomain = [0, d3.max(projected, (yDataPoint: DataPoint) => yDataPoint.amount)!];
  const yRange = [0, constants.chart.projection.height];
  const y = d3.scaleLinear().domain(yDomain).range(yRange);

  return [x, y] as const;
};

export function generateIntervalLabels(numbers: Array<number>, interval: number) {
  const result: Array<number> = [];

  // Add the first value of the input array
  result.push(numbers[0]);

  // Calculate the number of steps based on the interval
  const numSteps = Math.floor((numbers[numbers.length - 1] - numbers[0]) / interval);

  // Generate the numbers with the consistent interval
  for (let i = 0; i < numSteps; i++) {
    const current = result[result.length - 1] + interval;

    result.push(current);
  }

  // Add the last value of the input array
  // result.push(numbers[numbers.length - 1]);

  return result;
}

export const buildLinearChart = ({
  data,
  label,
  difference: differenceProp,
  percentage: percentageProp,
  width = constants.chart.performance.width,
  height = constants.chart.performance.height,
}: BuildLinearChartParams): LinearChartProps => {
  const difference = differenceProp ?? 0;
  const percentage = percentageProp ?? 0;

  const nodes = sanitizeNodes(data);

  const dataPoints = nodes.map<[number, number]>((node) => [node.value, node.index]);

  const yValues = dataPoints.map((value) => value[0]);
  const xValues = dataPoints.map((value) => value[1]);

  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);

  const scaleY = scaleLinear().domain([minY, maxY]).range([height, 0]);
  const scaleX = scaleLinear().domain([minX, maxX]).range([0, width]);

  const path = shape
    .line()
    .x(([, x]) => scaleX(x))
    .y(([y]) => scaleY(y))
    .curve(shape.curveBasis)(dataPoints);

  const shadowMaxX = (width / maxX) * dataPoints.length - 1;
  const shadowMaxY = constants.chart.performance.height * 100;

  const pathShadow = path ? path + `L${shadowMaxX}, ${shadowMaxY} L0,${shadowMaxY} Z` : null;

  const valid = nodes.length > 0 && nodes.every((n) => n && n.value && n.timestamp);

  return {
    path,
    label,
    width,
    height,
    valid,
    pathShadow,
    nodes,
    difference,
    percentage,
  };
};

export const sanitizeNodes = (data: Array<PortfolioPerformanceGraphNode | null> | null = []) => {
  if (!data) return [];

  return data?.map((n, index) => ({ ...n!, index }));
};

export const trimChartData = ({
  data = [],
  numberOfNodes = constants.chart.performance.nodes,
}: TrimChartDataParams) => {
  if (!data.length) return [];

  const interval = data.length / numberOfNodes;
  const result: Array<LinearChartNode> = [];

  let index = 0;

  for (let i = 0; i < numberOfNodes; i++) {
    result.push(data[Math.round(index)]);
    index += interval;
  }

  return result;
};

export const inflateChatData = ({
  data = [],
  numberOfNodes = constants.chart.performance.nodes,
}: InflateChartDataParams) => {
  if (data.length >= numberOfNodes) return data;
  if (data.length < 2) return [];

  const multiplier = Math.ceil(numberOfNodes / data.length);

  const result: Array<LinearChartNode> = [];

  data.forEach((d, index) => {
    for (let i = 0; i < multiplier; i++) {
      result.push({ ...d, index });
    }
  });

  return result;
};

export const calculateProjectedAmount = (params: ProjectedProgressionParams) => {
  const data = calculateProjectedProgression(params);
  return data[data.length - 1].amount;
};
